import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { establishmentsApi } from "../services/establishments";
import type { Establishment } from "../types/establishments";
import BrandLogo from "../components/BrandLogo";
import { currencySymbol } from "../utils/currency";
import "./EstablishmentPage.css";
import { absMediaUrl } from "../utils/mediaUrl";
import { t } from "../i18n";
import { DEFAULT_AVATAR_SRC, showDefaultAvatar } from "../lib/defaultAvatar";

type EstData = Establishment;

export default function EstablishmentPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [est, setEst] = useState<EstData | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState(false);
  // Tonight's talent set in the admin Venues tab: the venue QR goes straight to them.
  const onNow = est?.onNow || null;

  const load = useCallback(() => {
    if (!slug) return;
    setLoading(true);
    setNotFound(false);
    setLoadError(false);
    establishmentsApi
      .get(slug)
      .then((r) => setEst(r))
      .catch((err) => {
        // 404 = venue genuinely gone (deleted / bad slug) — a dead end.
        // Anything else (venue wifi drop, timeout, 5xx) is retryable.
        if (err?.response?.status === 404) setNotFound(true);
        else setLoadError(true);
      })
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (onNow) navigate(`/dj/${encodeURIComponent(onNow)}/request`, { replace: true });
  }, [onNow, navigate]);

  if (loading || onNow) {
    return (
      <div className="est-page">
        <div className="est-loader">
          <div className="est-spinner" />
        </div>
      </div>
    );
  }

  if (notFound || loadError || !est) {
    return (
      <div className="est-page">
        <div className="est-container">
          <div className="est-error">
            <BrandLogo className="est-wordmark" alt={t("establishmentPage.requestApp")} />
            <h2>{notFound ? t("establishmentPage.venueNotFound") : t("establishmentPage.couldnTLoadThis")}</h2>
            <p>
              {notFound
                ? t("establishmentPage.thisVenuePageIsn")
                : t("establishmentPage.checkYourConnectionAnd")}
            </p>
            {!notFound && (
              <button type="button" className="est-retry-btn" onClick={load}>
                {t("establishmentPage.tryAgain")}
              </button>
            )}
          </div>
          <footer className="est-footer">
            <span>{t("establishmentPage.poweredBy")}</span>
            <strong>{t("establishmentPage.requestApp")}</strong>
          </footer>
        </div>
      </div>
    );
  }

  return (
    <div className="est-page">
      <div className="est-container">
        <header className="est-header">
          <BrandLogo className="est-wordmark" alt={t("establishmentPage.requestApp")} />
          <h1 className="est-title">{est.name}</h1>
          <p className="est-subtitle">{t("establishmentPage.tapANameTo")}</p>
        </header>

        {est.djs.length === 0 ? (
          <p className="est-empty">{t("establishmentPage.noTalentHereYet")}</p>
        ) : (
          <div className="est-grid">
            {est.djs.map((dj) => {
              const avatar = absMediaUrl(dj.avatarUrl);
              const price = dj.pricePerRequest ?? 1;
              return (
                <div
                  key={dj._id}
                  className="est-dj-card"
                  onClick={() => navigate(`/dj/${dj.slug}/request`)}
                >
                  <div className="est-dj-avatar-wrap">
                    <img
                      src={avatar || DEFAULT_AVATAR_SRC}
                      alt={dj.name}
                      className="est-dj-avatar"
                      onError={showDefaultAvatar}
                    />
                  </div>
                  <div className="est-dj-info">
                    <h3 className="est-dj-name">{dj.name}</h3>
                    {dj.bio && <p className="est-dj-bio">{dj.bio}</p>}
                  </div>
                  <div className="est-dj-price">
                    {t("establishmentPage.from", { amount: currencySymbol(dj.currency), price })}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <footer className="est-footer">
          <span>{t("establishmentPage.poweredBy")}</span>
          <strong>{t("establishmentPage.requestApp")}</strong>
        </footer>
      </div>
    </div>
  );
}
